/**
 * Input-validering for sikkerhet
 */

/**
 * Valider konkurransetittel
 */
export const validateCompetitionTitle = (title) => {
  if (!title || typeof title !== 'string') {
    throw new Error('Tittel er påkrevd');
  }
  
  const trimmed = title.trim();
  
  if (trimmed.length === 0) {
    throw new Error('Tittel kan ikke være tom');
  }
  
  if (trimmed.length > 100) {
    throw new Error('Tittel kan ikke være lengre enn 100 tegn');
  }
  
  // Fjern potensielt farlige tegn
  const sanitized = trimmed.replace(/[<>]/g, '');
  
  return sanitized;
};

/**
 * Valider beskrivelse
 */
export const validateDescription = (description) => {
  if (!description) return '';
  
  if (typeof description !== 'string') {
    throw new Error('Beskrivelse må være tekst');
  }
  
  const trimmed = description.trim();
  
  if (trimmed.length > 500) {
    throw new Error('Beskrivelse kan ikke være lengre enn 500 tegn');
  }
  
  // Fjern potensielt farlige tegn
  const sanitized = trimmed.replace(/[<>]/g, '');
  
  return sanitized;
};

/**
 * Valider chat-melding
 */
export const validateChatMessage = (message) => {
  if (!message || typeof message !== 'string') {
    throw new Error('Melding er påkrevd');
  }
  
  const trimmed = message.trim();
  
  if (trimmed.length === 0) {
    throw new Error('Melding kan ikke være tom');
  }
  
  if (trimmed.length > 500) {
    throw new Error('Melding kan ikke være lengre enn 500 tegn');
  }
  
  // Fjern potensielt farlige tegn
  const sanitized = trimmed.replace(/[<>]/g, '');
  
  return sanitized;
};

/**
 * Valider brukernavn
 */
export const validateUsername = (name) => {
  if (!name || typeof name !== 'string') {
    throw new Error('Navn er påkrevd');
  }
  
  const trimmed = name.trim();
  
  if (trimmed.length === 0) {
    throw new Error('Navn kan ikke være tomt');
  }
  
  if (trimmed.length > 50) {
    throw new Error('Navn kan ikke være lengre enn 50 tegn');
  }
  
  // Fjern potensielt farlige tegn
  const sanitized = trimmed.replace(/[<>]/g, '');
  
  return sanitized;
};

/**
 * Valider konkurransekode
 */
export const validateCompetitionCode = (code) => {
  if (!code || typeof code !== 'string') {
    throw new Error('Konkurransekode er påkrevd');
  }
  
  const trimmed = code.trim().toUpperCase();
  
  if (trimmed.length !== 10) {
    throw new Error('Konkurransekode må være 10 tegn');
  }
  
  // Kun tillat bokstaver og tall
  if (!/^[A-Z0-9]+$/.test(trimmed)) {
    throw new Error('Konkurransekode kan kun inneholde bokstaver og tall');
  }
  
  return trimmed;
};

/**
 * Valider mål (goal)
 */
export const validateGoal = (goal) => {
  // Tillat 0 for longest_distance mode
  if (goal === 0) return 0;
  
  const num = Number(goal);
  
  if (isNaN(num)) {
    throw new Error('Mål må være et tall');
  }
  
  if (num < 0) {
    throw new Error('Mål må være 0 eller større');
  }
  
  if (num > 10000) {
    throw new Error('Mål kan ikke være større enn 10000 km');
  }
  
  return num;
};

/**
 * Valider konkurransemodus
 */
export const validateCompetitionMode = (mode) => {
  const validModes = ['first_to_goal', 'longest_distance', 'tug_of_war'];
  
  if (!validModes.includes(mode)) {
    throw new Error('Ugyldig konkurransemodus');
  }
  
  return mode;
};

/**
 * Valider aktivitetsdata
 */
export const validateActivity = (activity) => {
  if (!activity || typeof activity !== 'object') {
    throw new Error('Ugyldig aktivitetsdata');
  }
  
  // Valider km
  const km = Number(activity.km);
  if (isNaN(km) || km <= 0 || km > 500) {
    throw new Error('Ugyldig distanse (må være mellom 0 og 500 km)');
  }
  
  // Valider duration
  const duration = Number(activity.duration);
  if (isNaN(duration) || duration <= 0 || duration > 86400) {
    throw new Error('Ugyldig varighet (må være mellom 0 og 24 timer)');
  }
  
  // Valider dato
  if (!activity.date || isNaN(new Date(activity.date).getTime())) {
    throw new Error('Ugyldig dato');
  }
  
  return {
    km: km,
    duration: duration,
    avg_heartrate: activity.avg_heartrate ? Number(activity.avg_heartrate) : null,
    max_heartrate: activity.max_heartrate ? Number(activity.max_heartrate) : null,
    avg_speed: activity.avg_speed ? Number(activity.avg_speed) : null,
    max_speed: activity.max_speed ? Number(activity.max_speed) : null,
    fastest_km_seconds: activity.fastest_km_seconds ? Number(activity.fastest_km_seconds) : null,
    date: activity.date,
    type: 'Run',
    source: String(activity.source || 'Manual'),
    stravaId: activity.stravaId ? String(activity.stravaId) : null,
    name: activity.name ? String(activity.name).substring(0, 100) : null
  };
};

/**
 * Sanitize HTML for sikker visning
 */
export const sanitizeHTML = (text) => {
  if (!text) return '';
  
  return String(text)
    .replace(/&/g, '&amp;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#x27;')
    .replace(/\//g, '&#x2F;');
};
